/**
 * The parcel, for display.
 *
 * The geometry that MATTERS -- rows fitted to the boundary, the ground plane --
 * is computed once, on the server, by pv_core/parcel.py. This file holds only
 * what the map needs to show while the user is still drawing: the area in
 * hectares under the cursor, and the GeoJSON shape the project stores. It
 * repeats the equirectangular projection for that one purpose, and nothing
 * downstream reads its answer.
 */

const EARTH_RADIUS_M = 6371008.8;

/**
 * [[lon, lat], ...] from whatever the project stored, or null.
 *
 * `minPoints` defaults to a polygon's three. The map passes 1 so a boundary
 * that is still being traced -- one or two corners so far -- survives the
 * re-render between clicks instead of vanishing after the first one.
 */
export function ringFromParcel(parcel, { minPoints = 3 } = {}) {
  if (!parcel) return null;
  let ring = null;
  if (Array.isArray(parcel)) ring = parcel;
  else if (parcel.type === "Feature") return ringFromParcel(parcel.geometry, { minPoints });
  else if (parcel.type === "Polygon") ring = parcel.coordinates?.[0];
  if (!Array.isArray(ring)) return null;
  const pts = ring
    .map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : null))
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length > 1) {
    const [a, b] = [pts[0], pts[pts.length - 1]];
    if (a[0] === b[0] && a[1] === b[1]) pts.pop();
  }
  return pts.length >= minPoints ? pts : null;
}

/** GeoJSON Polygon, closed, from an open ring. */
export function parcelFromRing(ring) {
  if (!ring || ring.length < 3) return null;
  const closed = [...ring, ring[0]];
  return { type: "Polygon", coordinates: [closed.map(([lon, lat]) => [lon, lat])] };
}

/** Area in square metres of an open ring of [lon, lat]. */
export function ringAreaM2(ring) {
  if (!ring || ring.length < 3) return 0;
  const lat0 = ring.reduce((s, p) => s + p[1], 0) / ring.length;
  const lon0 = ring.reduce((s, p) => s + p[0], 0) / ring.length;
  const k = EARTH_RADIUS_M * Math.cos((lat0 * Math.PI) / 180);
  const xy = ring.map(([lon, lat]) => [
    ((lon - lon0) * Math.PI / 180) * k,
    ((lat - lat0) * Math.PI / 180) * EARTH_RADIUS_M,
  ]);
  let sum = 0;
  for (let i = 0; i < xy.length; i += 1) {
    const [x0, y0] = xy[i];
    const [x1, y1] = xy[(i + 1) % xy.length];
    sum += x0 * y1 - x1 * y0;
  }
  return Math.abs(sum) / 2;
}

/** Square metres first -- the unit the rest of the form uses -- hectares beside. */
export function describeArea(m2) {
  if (!m2) return "";
  const metres = `${Math.round(m2).toLocaleString("en-GB")} m²`;
  return m2 >= 10000 ? `${metres} (${(m2 / 10000).toFixed(2)} ha)` : metres;
}

/** The ring's centroid as [lat, lon] for a map to look at. */
export function ringCentre(ring) {
  if (!ring || !ring.length) return null;
  return [
    ring.reduce((s, p) => s + p[1], 0) / ring.length,
    ring.reduce((s, p) => s + p[0], 0) / ring.length,
  ];
}

/**
 * Each edge of the ring: its length in metres and its midpoint as [lat, lon],
 * for labelling the boundary on the map. Edge i runs from vertex i to i+1
 * (the last closes back to the first); an open trace of two points has one.
 */
export function ringEdges(ring, { closed = true } = {}) {
  if (!ring || ring.length < 2) return [];
  const n = closed && ring.length >= 3 ? ring.length : ring.length - 1;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const [lon0, lat0] = ring[i];
    const [lon1, lat1] = ring[(i + 1) % ring.length];
    const k = EARTH_RADIUS_M * Math.cos(((lat0 + lat1) / 2) * Math.PI / 180);
    const dx = ((lon1 - lon0) * Math.PI / 180) * k;
    const dy = ((lat1 - lat0) * Math.PI / 180) * EARTH_RADIUS_M;
    out.push({ index: i, lengthM: Math.hypot(dx, dy), mid: [(lat0 + lat1) / 2, (lon0 + lon1) / 2] });
  }
  return out;
}
